import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import clsx from 'clsx';
import { memo, useState } from 'react';
export const ExportButton = /*#__PURE__*/ memo(function ExportButton({ onExport, disabled = false }) {
    const [isOpen, setIsOpen] = useState(false);
    const [isExporting, setIsExporting] = useState(false);
    const handleExport = async (format)=>{
        setIsExporting(true);
        setIsOpen(false);
        try {
            await onExport(format);
        } catch  {
        // Error handling is delegated to the parent component via onExport
        } finally{
            setIsExporting(false);
        }
    };
    return /*#__PURE__*/ _jsxs("div", {
        className: "relative",
        children: [
            /*#__PURE__*/ _jsx("button", {
                "data-testid": "export-button",
                onClick: ()=>setIsOpen(!isOpen),
                disabled: disabled || isExporting,
                className: clsx('px-4 py-2 text-sm font-medium rounded-md transition-colors flex items-center gap-2', 'bg-white border border-gray-300 text-gray-700', 'hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500', (disabled || isExporting) && 'opacity-50 cursor-not-allowed'),
                children: isExporting ? /*#__PURE__*/ _jsxs(_Fragment, {
                    children: [
                        /*#__PURE__*/ _jsxs("svg", {
                            className: "animate-spin h-4 w-4",
                            fill: "none",
                            viewBox: "0 0 24 24",
                            role: "img",
                            "aria-label": "Exporting",
                            children: [
                                /*#__PURE__*/ _jsx("title", {
                                    children: "Exporting"
                                }),
                                /*#__PURE__*/ _jsx("circle", {
                                    className: "opacity-25",
                                    cx: "12",
                                    cy: "12",
                                    r: "10",
                                    stroke: "currentColor",
                                    strokeWidth: "4"
                                }),
                                /*#__PURE__*/ _jsx("path", {
                                    className: "opacity-75",
                                    fill: "currentColor",
                                    d: "M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                })
                            ]
                        }),
                        "Exporting..."
                    ]
                }) : /*#__PURE__*/ _jsxs(_Fragment, {
                    children: [
                        /*#__PURE__*/ _jsxs("svg", {
                            className: "w-4 h-4",
                            fill: "none",
                            stroke: "currentColor",
                            viewBox: "0 0 24 24",
                            role: "img",
                            "aria-label": "Export",
                            children: [
                                /*#__PURE__*/ _jsx("title", {
                                    children: "Export"
                                }),
                                /*#__PURE__*/ _jsx("path", {
                                    strokeLinecap: "round",
                                    strokeLinejoin: "round",
                                    strokeWidth: 2,
                                    d: "M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                                })
                            ]
                        }),
                        "Export"
                    ]
                })
            }),
            isOpen && !disabled && !isExporting && /*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    /*#__PURE__*/ _jsx("div", {
                        className: "fixed inset-0 z-10",
                        onClick: ()=>setIsOpen(false),
                        role: "button",
                        tabIndex: -1,
                        "aria-label": "Close export menu"
                    }),
                    /*#__PURE__*/ _jsx("div", {
                        "data-testid": "export-dropdown",
                        className: "absolute right-0 mt-2 w-56 bg-white rounded-md shadow-lg border border-gray-200 z-20",
                        children: /*#__PURE__*/ _jsxs("div", {
                            className: "py-1",
                            children: [
                                /*#__PURE__*/ _jsx("button", {
                                    onClick: ()=>handleExport('xlsx'),
                                    className: "block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors",
                                    children: /*#__PURE__*/ _jsxs("div", {
                                        className: "flex items-center gap-2",
                                        children: [
                                            /*#__PURE__*/ _jsxs("svg", {
                                                className: "w-4 h-4 text-green-600",
                                                fill: "currentColor",
                                                viewBox: "0 0 20 20",
                                                role: "img",
                                                "aria-label": "Excel",
                                                children: [
                                                    /*#__PURE__*/ _jsx("title", {
                                                        children: "Excel"
                                                    }),
                                                    /*#__PURE__*/ _jsx("path", {
                                                        d: "M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm3 4h2v2H7V7zm0 4h2v2H7v-2zm4-4h2v2h-2V7zm0 4h2v2h-2v-2z"
                                                    })
                                                ]
                                            }),
                                            "Export as Excel (.xlsx)"
                                        ]
                                    })
                                }),
                                /*#__PURE__*/ _jsx("button", {
                                    onClick: ()=>handleExport('csv'),
                                    className: "block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors",
                                    children: /*#__PURE__*/ _jsxs("div", {
                                        className: "flex items-center gap-2",
                                        children: [
                                            /*#__PURE__*/ _jsxs("svg", {
                                                className: "w-4 h-4 text-gray-600",
                                                fill: "currentColor",
                                                viewBox: "0 0 20 20",
                                                role: "img",
                                                "aria-label": "CSV",
                                                children: [
                                                    /*#__PURE__*/ _jsx("title", {
                                                        children: "CSV"
                                                    }),
                                                    /*#__PURE__*/ _jsx("path", {
                                                        d: "M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z"
                                                    })
                                                ]
                                            }),
                                            "Export as CSV"
                                        ]
                                    })
                                })
                            ]
                        })
                    })
                ]
            })
        ]
    });
});
